/**
 * transforms/authoringToViewer.ts — Conversion authoring → viewer (WRAPPER three.js).
 *
 * REVISED BOUNDARY RULE (SPEC §6, point 2): `transforms/` is the
 * wrapping on three.js. This function defines the CONVERSION ROTATION
 * which takes the component from the authoring X-Y plane to the viewer Y-Z plane
 * (SPEC G2): `auth x → viewer z`, `auth z → viewer −x`, `auth y → viewer y`.
 *
 * meshQuat / holesQuat SEPARATION (FIX A — VERY SERIOUS bug fixed):
 * - `holesQuat = authoringToViewerQuat()` = Ry(∓90°) conversion ONLY.
 * It is the HOLES and normals frame (and the fallback box). The euler of
 * component (`component_rotation_deg`) is NOT applied here: the holes
 * They must NOT receive body rotation (mirroring).
 * - `meshQuat = euler ∘ conversion` (conversion applied to frame authoring
 * which already contains the euler) = rotation of the GLB BODY. It is made in
 * Object3D hierarchy (model/): lock group with quaternion = conversion,
 * "mesh" child with quaternion = euler — see model/connectors.ts.
 *
 * SIGN (TODO to calibrate in AC-1): the sign of Ry(∓90°) is a CONSTANT in
 * `config/defaults.ts` (SSOT) — `AUTHORING_TO_VIEWER_ROTATION_DEG`. If in the test
 * visually the component is mirrored, reverse the sign ONLY there.*/

import * as THREE from "three";
import type { Vec3 } from "../data/types";
import { AUTHORING_TO_VIEWER_ROTATION_DEG } from "../config/defaults";
import { applyQuatToVec3, axisAngleQuat } from "./vectors";

/**
 * Quaternion conversion authoring→viewer per il lato JOIST: Ry(-90°) —
 * `holesQuat`.
 *
 * Used for:
 * - the position of the HOLES and their NORMALS (FIX A: no euler),
 * - the frame of the lock group (child), on which the GLB then adds the euler.
 *
 * The sign is the SSOT constant `AUTHORING_TO_VIEWER_ROTATION_DEG` (config/,
 * dove e' documentata la mappatura assi esatta, verificata con calcolo reale).
 * Vale SOLO per il joist — vedi `authoringToViewerQuatForHeader()` sotto per
 * il lato header (segno opposto).
 *
 * @returns THREE.Quaternion of the fixed conversion rotation.*/
export function authoringToViewerQuat(): THREE.Quaternion {
  return axisAngleQuat([0, 1, 0], AUTHORING_TO_VIEWER_ROTATION_DEG[1]);
}

/**
 * Quaternion conversion authoring→viewer per il lato HEADER: Ry(+90°) —
 * segno OPPOSTO al joist (`authoringToViewerQuat()`).
 *
 * Confermato dall'utente 25-09-2026 (indagine incastro): joist e header sono
 * posizionati specularmente rispetto al piano connettore condiviso, quindi la
 * loro conversione autoring→viewer naturale ha segno opposto — NON e' la
 * stessa rotazione riusata con un mirror/correzione aggiuntiva applicata dopo
 * (approccio precedente, sbagliato: una costante di "correzione" universale
 * applicata a entrambi i lati). Il joist non necessita di alcuna correzione
 * oltre a questa conversione base (per il joist si compone pero' anche il
 * flip mondiale Rx180, vedi `transforms/connectorPlacement.ts`).
 *
 * @returns THREE.Quaternion Ry(+90°) (negazione di `AUTHORING_TO_VIEWER_ROTATION_DEG[1]`).*/
export function authoringToViewerQuatForHeader(): THREE.Quaternion {
  return axisAngleQuat([0, 1, 0], -AUTHORING_TO_VIEWER_ROTATION_DEG[1]);
}

/**
 * Applies conversion only to an authoring position (pure tuple).
 * Does not include component rotation: useful for positioning the frame
 * of the connector plane and for the positions that only undergo the change of
 * coordinate system (module.position → offset in the viewer).
 *
 * @param position Position in the authoring frame [x, y, z].
 * @returns Position in the frame viewer (after Ry(∓90°)).*/
export function applyAuthoringToViewer(position: Vec3): Vec3 {
  return applyQuatToVec3(authoringToViewerQuat(), position);
}
